import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import api from '../../services/api';

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  slug: string | null;
  isActive: boolean;
  translatedName?: string;
}

interface ProductDisplay {
  id: string;
  name: string;
  image: string;
  link: string;
  price: number;
}

const OurProducts: React.FC = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState<ProductDisplay[]>([]);
  const [loading, setLoading] = useState(true);

  // Function to get product preview image
  const getProductPreviewImage = (product: Product) => {
    if (!product.category || !product.slug) return undefined;
    
    // Generate path for _1_small.jpg image
    return `/products/${product.category.slug}/${product.slug}/${product.category.slug}_1_small.jpg`;
  };

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/products');
        const productsData: Product[] = response.data;
        
        // Transform products for display
        const displayProducts: ProductDisplay[] = productsData.map(product => ({
          id: product.id,
          name: product.name,
          image: getProductPreviewImage(product) || '/images/placeholder-product.jpg',
          link: `/products/${product.category.slug}/${product.slug || product.name}`,
          price: product.price
        }));

        setProducts(displayProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);



  const renderProductCard = (product: ProductDisplay) => {
    return (
      <div className="col">
        <div className="col-holder bg-white rounded-lg shadow-lg overflow-hidden flex flex-col md:flex-row hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
          {/* Image Holder - Full width on mobile, half width on desktop */}
          <div className="img-holder w-full md:w-1/2 relative">
            <Link to={product.link}>
              <img 
                src={product.image} 
                alt={product.name}
                className="w-full h-64 object-cover"
              />
            </Link>
          </div>

          {/* Text Holder - Full width on mobile, half width on desktop */}
          <div className="text-holder w-full md:w-1/2 p-6 flex flex-col justify-center">
            <div className="product-info">
              <h2 className="title text-2xl font-bold mb-4">
                <Link to={product.link} className="text-gray-900 hover:text-green-600 transition-colors duration-300">
                  {product.name}
                </Link>
              </h2>
              <div className="meta-info space-y-2 mb-6">
                <span className="category flex items-center text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
                  </svg>
                  {t(`products.categories.${product.id === 'wine' ? 'wine' : 'wine'}`) || 'Vini'}
                </span>
                <span className="stock flex items-center text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  Disponibile
                </span>
              </div>
            </div>

            <div className="btn-holder">
              <Link 
                to={product.link} 
                className="primary-btn readmore-btn bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 inline-block"
              >
                {t('common.viewDetails') || 'Vedi Dettagli'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <section id="products" className="our-products py-16 bg-white">
        <div className="container mx-auto px-4">
          <header className="section-header text-center mb-12">
            <h2 className="section-title text-4xl font-bold text-gray-900 mb-4">
              {t('landing.products.title') || 'I Nostri Prodotti'}
            </h2>
          </header>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Caricamento prodotti...</p>
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section id="products" className="deals-section py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <header className="section-header text-center mb-12">
          <h2 className="section-title text-4xl font-bold text-gray-900 mb-4">
            {t('landing.products.title') || 'I Nostri Prodotti'}
          </h2>
          <div className="section-content">
            <p className="text-gray-600 text-lg">
              {t('landing.products.subtitle') || 'Scopri i sapori autentici dell\'Abruzzo con la nostra selezione di prodotti tipici'}
            </p>
          </div>
        </header>

        {/* Products Grid - Same style as OurTrips */}
        <div className="space-y-8 max-w-4xl mx-auto">
          {products.map((product) => (
            <div key={product.id}>
              {renderProductCard(product)}
            </div>
          ))}
        </div>


      </div>
    </section>
  );
};

export default OurProducts;
